<template>
  <div v-redirect-if-no-permission:MANAGE_FINANCE_ACCOUNTS class="fin-page">
    <finance-section-nav />
    <loading-component :value="loading" />

    <v-dialog v-model="dialog" max-width="760px" persistent>
      <v-card>
        <v-card-title>
          {{ editedAccountId ? $t("finance.coa.editAccount") : $t("finance.coa.addAccount") }}
          <v-chip v-if="editedSystemKey" small color="orange" text-color="white" class="ms-2">
            {{ $t("finance.coa.systemAccount") }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <v-layout wrap>
            <v-flex xs12 md4 class="pa-2">
              <v-text-field v-model="form.code" :label="$t('finance.coa.accountCode')" />
            </v-flex>
            <v-flex xs12 md4 class="pa-2">
              <v-select
                v-model="form.kind"
                :items="kindOptions"
                item-text="text"
                item-value="value"
                :label="$t('finance.coa.kind')"
                @change="onKindChange"
              />
            </v-flex>
            <v-flex xs12 md4 class="pa-2">
              <v-select
                v-model="form.nature"
                :items="natureOptions"
                item-text="text"
                item-value="value"
                :label="$t('finance.coa.nature')"
                :disabled="!!editedSystemKey"
              />
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field v-model="form.name" :label="$t('finance.coa.accountNameEn')" />
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field v-model="form.name_ar" :label="$t('finance.coa.accountNameAr')" />
            </v-flex>
            <v-flex v-if="form.kind === 'sub'" xs12 md6 class="pa-2">
              <v-autocomplete
                v-model="form.parent_id"
                :items="parentAccountOptions"
                item-text="text"
                item-value="value"
                :label="$t('finance.coa.parentAccount')"
                clearable
              />
            </v-flex>
            <v-flex v-if="form.kind !== 'closing'" xs12 md6 class="pa-2">
              <v-autocomplete
                v-model="form.closing_account_id"
                :items="closingAccountOptions"
                item-text="text"
                item-value="value"
                :label="$t('finance.coa.closingAccount')"
                clearable
              />
            </v-flex>
            <v-flex v-else xs12 md6 class="pa-2">
              <v-autocomplete
                v-model="form.closing_account_id"
                :items="closingAccountOptions"
                item-text="text"
                item-value="value"
                :label="$t('finance.coa.closingAccountOptional')"
                clearable
              />
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-select
                v-model="form.type"
                :items="typeOptions"
                item-text="text"
                item-value="value"
                :label="$t('finance.coa.financialType')"
                :disabled="!!editedSystemKey"
              />
            </v-flex>
            <v-flex xs12 md3 class="pa-2">
              <v-switch v-model="form.is_active" :label="$t('finance.common.status')" />
            </v-flex>
            <v-flex xs12 md3 class="pa-2">
              <v-switch v-model="form.allows_manual_posting" :label="$t('finance.coa.manualPosting')" />
            </v-flex>
            <v-flex xs12 class="pa-2">
              <v-textarea v-model="form.description" :label="$t('finance.coa.description')" auto-grow rows="2" />
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat :disabled="saving" @click="closeDialog">{{ $t("finance.common.cancel") }}</v-btn>
          <v-btn color="primary" :loading="saving" @click="submitAccount">{{ $t("finance.coa.saveAccount") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Renumbering onto a taken code: offer to trade codes with the holder. -->
    <v-dialog v-model="swapDialogOpen" max-width="520px" persistent>
      <v-card>
        <v-card-title>{{ $t("finance.coa.codeConflictTitle") }}</v-card-title>
        <v-card-text v-if="swapConflict">
          {{
            $t("finance.coa.codeConflictBody", {
              code: swapConflict.code,
              name: displayAccountName(swapConflict),
            })
          }}
          <div class="mt-2">{{ $t("finance.coa.swapCodesHint") }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat :disabled="saving" @click="cancelSwap">{{ $t("finance.common.cancel") }}</v-btn>
          <v-btn color="primary" :loading="saving" @click="confirmSwap">
            {{ $t("finance.coa.swapCodes") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialogOpen" max-width="480px" persistent>
      <v-card>
        <v-card-title>{{ $t("finance.coa.deleteConfirmTitle") }}</v-card-title>
        <v-card-text v-if="deleteTarget">
          {{
            $t("finance.coa.deleteConfirmBody", {
              code: deleteTarget.code,
              name: displayAccountName(deleteTarget),
            })
          }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat :disabled="deleting" @click="closeDeleteDialog">{{ $t("finance.common.cancel") }}</v-btn>
          <v-btn color="error" :loading="deleting" @click="confirmDelete">
            {{ $t("finance.coa.deleteAccount") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="closeDialogOpen" max-width="820px" persistent>
      <v-card>
        <v-card-title>{{ $t("finance.coa.periodClose") }}</v-card-title>
        <v-card-text>
          <v-layout wrap>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field v-model="closeForm.from" type="date" :label="$t('finance.common.from')" />
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field v-model="closeForm.to" type="date" :label="$t('finance.common.to')" />
            </v-flex>
          </v-layout>

          <div v-if="closePreview">
            <p v-if="!closePreview.entries.length" class="fin-empty">
              {{ $t("finance.coa.periodCloseNothing") }}
            </p>
            <div v-for="entry in closePreview.entries" :key="entry.destination_id" class="mb-3">
              <strong>{{ entry.memo }}</strong>
              <table class="fin-table mt-1">
                <thead>
                  <tr>
                    <th>{{ $t("finance.coa.account") }}</th>
                    <th class="fin-num-head">{{ $t("finance.journals.debit") }}</th>
                    <th class="fin-num-head">{{ $t("finance.journals.credit") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(line, index) in entry.lines" :key="index">
                    <td>{{ lineAccountLabel(line.gl_account_id) }}</td>
                    <td class="fin-num">{{ line.entry_type === "debit" ? formatAmount(line.amount) : "" }}</td>
                    <td class="fin-num">{{ line.entry_type === "credit" ? formatAmount(line.amount) : "" }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat @click="closePeriodDialog">{{ $t("finance.common.cancel") }}</v-btn>
          <v-btn color="primary" flat :loading="closing" @click="previewPeriodClose">
            {{ $t("finance.coa.periodClosePreview") }}
          </v-btn>
          <v-btn
            color="error"
            :loading="closing"
            :disabled="!closePreview || !closePreview.entries.length"
            @click="confirmPeriodClose"
          >
            {{ $t("finance.coa.periodCloseConfirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card>
      <div class="fin-header fin-header--sticky coa-no-print">
        <div class="fin-toolbar">
          <span class="fin-title">{{ $t("finance.coa.chartOfAccounts") }}</span>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="expandAll">
              <v-icon small>unfold_more</v-icon>
            </v-btn>
            <span>{{ $t("finance.coa.expandAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="collapseAll">
              <v-icon small>unfold_less</v-icon>
            </v-btn>
            <span>{{ $t("finance.coa.collapseAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="exportExcel">
              <v-icon small>grid_on</v-icon>
            </v-btn>
            <span>Excel</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="exportPdf">
              <v-icon small>picture_as_pdf</v-icon>
            </v-btn>
            <span>PDF</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat color="primary" @click="loadAccounts">
              <v-icon small>refresh</v-icon>
            </v-btn>
            <span>{{ $t("finance.common.refresh") }}</span>
          </v-tooltip>
          <v-btn small flat color="error" @click="openPeriodClose">{{ $t("finance.coa.periodClose") }}</v-btn>
          <v-btn small color="primary" @click="openCreateDialog()">{{ $t("finance.coa.addAccount") }}</v-btn>
        </div>
        <div class="fin-filters">
          <v-text-field
            v-model="search"
            :label="$t('finance.coa.searchTree')"
            append-icon="search"
            clearable
            hide-details
            class="fin-field--wide"
          />
          <v-select
            v-model="filterKind"
            :items="[{ text: $t('finance.coa.allKinds'), value: null }].concat(kindOptions)"
            item-text="text"
            item-value="value"
            hide-details
            class="fin-field"
          />
          <v-select
            v-model="filterNature"
            :items="[{ text: $t('finance.coa.allNatures'), value: null }].concat(natureOptions)"
            item-text="text"
            item-value="value"
            hide-details
            class="fin-field"
          />
        </div>
      </div>

      <div class="coa-table-wrap fin-table-wrap">
        <table class="coa-tree-table fin-table">
          <thead>
            <tr>
              <th>{{ $t("finance.common.code") }}</th>
              <th class="coa-col-name">{{ $t("finance.common.name") }}</th>
              <th>{{ $t("finance.coa.level") }}</th>
              <th>{{ $t("finance.coa.kind") }}</th>
              <th>{{ $t("finance.coa.nature") }}</th>
              <th>{{ $t("finance.coa.closingAccount") }}</th>
              <th>{{ $t("finance.common.status") }}</th>
              <th class="coa-no-print">{{ $t("finance.common.actions") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in visibleRows"
              :key="row.account.id"
              :class="rowClasses(row)"
            >
              <td class="fin-code">{{ row.account.code }}</td>
              <td class="coa-col-name">
                <span class="coa-indent" :style="{ 'padding-inline-start': row.depth * 26 + 'px' }">
                  <v-btn
                    v-if="row.hasChildren"
                    icon
                    small
                    class="coa-toggle coa-no-print"
                    @click="toggleNode(row.account.id)"
                  >
                    <v-icon small>{{ isExpanded(row.account.id) ? "remove" : "add" }}</v-icon>
                  </v-btn>
                  <span v-else class="coa-toggle-spacer"></span>
                  <span class="fin-ellipsis" :title="displayAccountName(row.account)">{{ displayAccountName(row.account) }}</span>
                </span>
              </td>
              <td>{{ row.account.level }}</td>
              <td>{{ formatKind(row.account.kind) }}</td>
              <td>{{ formatNature(row.account.nature) }}</td>
              <td>{{ closingLabel(row.account) }}</td>
              <td>{{ row.account.is_active ? $t("finance.coa.active") : $t("finance.coa.inactive") }}</td>
              <td class="coa-actions coa-no-print">
                <v-btn small icon class="ma-0" :title="$t('finance.coa.addChild')" @click="openCreateDialog(row.account)">
                  <v-icon small color="primary">playlist_add</v-icon>
                </v-btn>
                <v-btn small icon class="ma-0" :title="$t('finance.coa.editAccount')" @click="openEditDialog(row.account)">
                  <v-icon small color="primary">edit</v-icon>
                </v-btn>
                <v-btn small icon class="ma-0" :title="$t('finance.coa.deleteAccount')" @click="openDeleteDialog(row.account)">
                  <v-icon small color="error">delete</v-icon>
                </v-btn>
              </td>
            </tr>
            <tr v-if="!visibleRows.length && !loading">
              <td colspan="8" class="fin-empty">{{ $t("finance.coa.noResults") }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </v-card>
  </div>
</template>

<script>
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";
import LoadingComponent from "@/components/LoadingComponent";
import { companyNow, companyToday } from "@/util/companyTime";

export default {
  name: "ChartOfAccounts",
  title() {
    return this.$t("finance.coa.pageTitle");
  },
  components: { FinanceSectionNav, LoadingComponent },
  data() {
    return {
      loading: false,
      saving: false,
      dialog: false,
      editedAccountId: null,
      editedSystemKey: null,
      accounts: [],
      form: this.emptyForm(),
      search: "",
      filterKind: null,
      filterNature: null,
      // id -> true. Replaced wholesale (not mutated) so Vue 2 reactivity works.
      expandedIds: {},
      closeDialogOpen: false,
      closing: false,
      closeForm: { from: null, to: null },
      closePreview: null,
      swapDialogOpen: false,
      swapConflict: null,
      pendingPayload: null,
      pendingAccountId: null,
      deleteDialogOpen: false,
      deleteTarget: null,
      deleting: false,
    };
  },
  computed: {
    typeOptions() {
      return [
        { text: this.$t("finance.coa.typeAsset"), value: "ASSET" },
        { text: this.$t("finance.coa.typeLiability"), value: "LIABILITY" },
        { text: this.$t("finance.coa.typeEquity"), value: "EQUITY" },
        { text: this.$t("finance.coa.typeRevenue"), value: "REVENUE" },
        { text: this.$t("finance.coa.typeExpense"), value: "EXPENSE" },
      ];
    },
    kindOptions() {
      return [
        { text: this.$t("finance.coa.kindMain"), value: "main" },
        { text: this.$t("finance.coa.kindSub"), value: "sub" },
        { text: this.$t("finance.coa.kindClosing"), value: "closing" },
      ];
    },
    natureOptions() {
      return [
        { text: this.$t("finance.coa.balanceDebit"), value: "debit" },
        { text: this.$t("finance.coa.balanceCredit"), value: "credit" },
        { text: this.$t("finance.coa.natureBoth"), value: "both" },
        { text: this.$t("finance.coa.natureNone"), value: "none" },
      ];
    },
    parentAccountOptions() {
      // An account cannot be re-parented onto itself or its own subtree, and
      // closing accounts cannot be parents (BRD).
      const blocked = this.editedAccountId
        ? this.descendantIds(this.editedAccountId)
        : {};
      if (this.editedAccountId) {
        blocked[this.editedAccountId] = true;
      }

      return this.accounts
        .filter((account) => !blocked[account.id] && account.kind !== "closing")
        .map((account) => ({
          text: this.accountOptionLabel(account),
          value: account.id,
        }));
    },
    closingAccountOptions() {
      return this.accounts
        .filter((account) => account.kind === "closing" && account.id !== this.editedAccountId)
        .map((account) => ({
          text: this.accountOptionLabel(account),
          value: account.id,
        }));
    },
    accountsById() {
      const map = {};
      this.accounts.forEach((account) => {
        map[account.id] = account;
      });
      return map;
    },
    childrenByParent() {
      const map = {};
      this.accounts.forEach((account) => {
        // Orphaned parent_id (deleted/missing parent) -> treat as root.
        const parentId =
          account.parent_id && this.accountsById[account.parent_id] ? account.parent_id : 0;
        if (!map[parentId]) {
          map[parentId] = [];
        }
        map[parentId].push(account);
      });
      // BRD sorting rule: ascending by code on every level, compared as
      // hierarchical digit strings (NOT numerically): the statement roots
      // 000/001/002 must stay together before account class 1, so "002"
      // sorts by its leading zeros, not by its value 2.
      const cmp = (a, b) => {
        const codeA = String(a.code);
        const codeB = String(b.code);
        if (codeA === codeB) {
          return 0;
        }
        return codeA < codeB ? -1 : 1;
      };
      Object.keys(map).forEach((key) => map[key].sort(cmp));
      return map;
    },
    // Accounts matching search + kind/nature filters, plus all their ancestors
    // (kept so the tree shape stays readable) and their whole subtrees (for
    // text search only; attribute filters match exact rows + ancestors).
    searchVisibleIds() {
      const term = (this.search || "").trim().toLowerCase();
      if (!term && !this.filterKind && !this.filterNature) {
        return null;
      }
      const visible = {};
      const matches = this.accounts.filter((account) => {
        const textMatch =
          !term ||
          String(account.code).toLowerCase().indexOf(term) !== -1 ||
          String(account.name || "").toLowerCase().indexOf(term) !== -1 ||
          String(account.name_ar || "").toLowerCase().indexOf(term) !== -1;
        const kindMatch = !this.filterKind || account.kind === this.filterKind;
        const natureMatch = !this.filterNature || account.nature === this.filterNature;
        return textMatch && kindMatch && natureMatch;
      });
      matches.forEach((account) => {
        if (term && !this.filterKind && !this.filterNature) {
          // pure text search keeps the whole subtree visible
          Object.assign(visible, this.descendantIds(account.id));
        }
        visible[account.id] = true;
        // ancestor chain (cycle-safe)
        let cursor = account.parent_id;
        let steps = 0;
        while (cursor && this.accountsById[cursor] && steps < 100) {
          visible[cursor] = true;
          cursor = this.accountsById[cursor].parent_id;
          steps++;
        }
      });
      return visible;
    },
    visibleRows() {
      const searching = this.searchVisibleIds;
      const rows = [];
      const walk = (parentId, depth, seen) => {
        (this.childrenByParent[parentId] || []).forEach((account) => {
          if (seen[account.id]) {
            return; // cycle guard
          }
          if (searching && !searching[account.id]) {
            return;
          }
          const hasChildren = !!(this.childrenByParent[account.id] || []).length;
          rows.push({ account, depth, hasChildren });
          // While filtering every branch is forced open so matches are visible.
          if (hasChildren && (searching || this.expandedIds[account.id])) {
            const nextSeen = Object.assign({}, seen);
            nextSeen[account.id] = true;
            walk(account.id, depth + 1, nextSeen);
          }
        });
      };
      walk(0, 0, {});
      return rows;
    },
  },
  mounted() {
    this.loadAccounts();
  },
  methods: {
    emptyForm() {
      return {
        code: null,
        name: null,
        name_ar: null,
        type: "ASSET",
        kind: "sub",
        nature: "debit",
        parent_id: null,
        closing_account_id: null,
        description: null,
        is_active: true,
        allows_manual_posting: true,
      };
    },
    rowClasses(row) {
      return {
        "coa-level-1": row.account.level === 1,
        "coa-level-2": row.account.level === 2,
        "coa-parent-row": row.hasChildren,
        "coa-inactive-row": !row.account.is_active,
      };
    },
    onKindChange(kind) {
      if (kind !== "sub") {
        this.form.parent_id = null;
      }
      if (kind === "closing") {
        this.form.closing_account_id = null;
      }
    },
    descendantIds(accountId) {
      const result = {};
      const walk = (id) => {
        (this.childrenByParent[id] || []).forEach((child) => {
          if (!result[child.id]) {
            result[child.id] = true;
            walk(child.id);
          }
        });
      };
      walk(accountId);
      return result;
    },
    isExpanded(id) {
      return !!this.expandedIds[id];
    },
    toggleNode(id) {
      const next = Object.assign({}, this.expandedIds);
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = true;
      }
      this.expandedIds = next;
    },
    expandAll() {
      const next = {};
      this.accounts.forEach((account) => {
        next[account.id] = true;
      });
      this.expandedIds = next;
    },
    collapseAll() {
      this.expandedIds = {};
    },
    loadAccounts() {
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_CHART_OF_ACCOUNTS)
        .then(
          function (response) {
            this.accounts = response.data.data || [];
            // Accountants read the whole structure by default.
            this.expandAll();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.coa.failedLoad")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    getErrorMessage(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      return fallback;
    },
    openCreateDialog(parentAccount) {
      this.editedAccountId = null;
      this.editedSystemKey = null;
      this.form = this.emptyForm();
      if (parentAccount) {
        // New sub-account: inherit placement and accounting nature from the parent.
        this.form.kind = "sub";
        this.form.parent_id = parentAccount.id;
        this.form.type = parentAccount.type;
        this.form.nature = parentAccount.nature || parentAccount.normal_balance;
        this.form.closing_account_id = parentAccount.closing_account_id || null;
      }
      this.dialog = true;
    },
    openEditDialog(account) {
      this.editedAccountId = account.id;
      this.editedSystemKey = account.system_key || null;
      this.form = {
        code: account.code,
        name: account.name,
        name_ar: account.name_ar,
        type: account.type,
        kind: account.kind || (account.parent_id ? "sub" : "main"),
        nature: account.nature || account.normal_balance,
        parent_id: account.parent_id,
        closing_account_id: account.closing_account_id,
        description: account.description,
        is_active: !!account.is_active,
        allows_manual_posting: !!account.allows_manual_posting,
      };
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      this.editedAccountId = null;
      this.editedSystemKey = null;
      this.form = this.emptyForm();
    },
    submitAccount() {
      if (!this.form.code || !this.form.name || !this.form.kind || !this.form.nature) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.coa.requiredFields"), "red");
        return;
      }
      // BRD field rules, mirrored client-side for friendlier feedback.
      if (this.form.kind === "sub" && !this.form.parent_id) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.coa.parentRequired"), "red");
        return;
      }
      if (this.form.kind !== "closing" && !this.form.closing_account_id) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.coa.closingRequired"), "red");
        return;
      }

      // The dialog hides the parent/closing pickers for kinds that must not
      // have them, so send explicit nulls: otherwise a stale parent left on a
      // pre-BRD row (e.g. the P&L closing account at level 2) is resent as-is
      // and the backend rejects every edit of that account.
      const payload = Object.assign({}, this.form);
      if (payload.kind !== "sub") {
        payload.parent_id = null;
      }
      this.sendAccountRequest(this.editedAccountId, payload);
    },
    // accountId is captured per request (not read from editedAccountId at
    // response time): a late code-conflict response must never attach its
    // swap offer — or the confirmed retry — to a different account's dialog.
    sendAccountRequest(accountId, payload) {
      this.saving = true;
      const request = accountId
        ? this.axios.put(this.api.ACTIONS.ACCOUNTING_CHART_OF_ACCOUNT(accountId), payload)
        : this.axios.post(this.api.ACTIONS.ACCOUNTING_CHART_OF_ACCOUNTS, payload);

      request
        .then(
          function () {
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.coa.accountSaved"), "green");
            this.cancelSwap();
            this.closeDialog();
            this.loadAccounts();
          }.bind(this)
        )
        .catch(
          function (error) {
            const conflict =
              accountId &&
              error &&
              error.response &&
              error.response.status === 422 &&
              error.response.data &&
              error.response.data.code_conflict;
            if (conflict) {
              this.swapConflict = error.response.data.code_conflict;
              this.pendingPayload = payload;
              this.pendingAccountId = accountId;
              this.swapDialogOpen = true;
              return;
            }
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.coa.failedSave")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.saving = false;
          }.bind(this)
        );
    },
    confirmSwap() {
      if (!this.pendingPayload || !this.pendingAccountId) {
        this.cancelSwap();
        return;
      }
      // swap_with_id pins the swap to the holder the user just confirmed —
      // the backend refuses if the code changed hands meanwhile.
      this.sendAccountRequest(
        this.pendingAccountId,
        Object.assign({}, this.pendingPayload, {
          swap_code: true,
          swap_with_id: this.swapConflict ? this.swapConflict.id : null,
        })
      );
    },
    cancelSwap() {
      this.swapDialogOpen = false;
      this.swapConflict = null;
      this.pendingPayload = null;
      this.pendingAccountId = null;
    },
    displayAccountName(account) {
      if (!account) {
        return "-";
      }

      if (this.$i18n && this.$i18n.locale === "ar" && account.name_ar) {
        return account.name_ar;
      }

      return account.name || account.name_ar || "-";
    },
    accountOptionLabel(account) {
      return account.code + " - " + this.displayAccountName(account);
    },
    lineAccountLabel(accountId) {
      const account = this.accountsById[accountId];
      return account ? this.accountOptionLabel(account) : accountId;
    },
    closingLabel(account) {
      if (!account.closing_account_id) {
        return "-";
      }
      const target = this.accountsById[account.closing_account_id];
      return target ? this.displayAccountName(target) : "-";
    },
    formatKind(value) {
      const map = {
        main: this.$t("finance.coa.kindMain"),
        sub: this.$t("finance.coa.kindSub"),
        closing: this.$t("finance.coa.kindClosing"),
      };

      return map[value] || value || "-";
    },
    formatNature(value) {
      const map = {
        debit: this.$t("finance.coa.balanceDebit"),
        credit: this.$t("finance.coa.balanceCredit"),
        both: this.$t("finance.coa.natureBoth"),
        none: this.$t("finance.coa.natureNone"),
      };

      return map[value] || value || "-";
    },
    formatAmount(value) {
      return this.formatMoney(value);
    },
    // The whole tree in display order (ignores search/expand state): exports
    // always carry the full chart, not whatever happens to be on screen.
    fullTreeRows() {
      const rows = [];
      const walk = (parentId, depth, seen) => {
        (this.childrenByParent[parentId] || []).forEach((account) => {
          if (seen[account.id]) {
            return; // cycle guard
          }
          rows.push({ account, depth });
          const nextSeen = Object.assign({}, seen);
          nextSeen[account.id] = true;
          walk(account.id, depth + 1, nextSeen);
        });
      };
      walk(0, 0, {});
      return rows;
    },
    exportFileStamp() {
      // Final-version doc 2026-10-05: company day, not the PC calendar.
      return companyToday();
    },
    exportExcel() {
      // Real .xlsx built client-side from the loaded tree, in tree order with
      // indentation and level styling (the old CSV endpoint stays available
      // on the API for integrations).
      const rows = this.fullTreeRows();
      const isArabic = this.$i18n && this.$i18n.locale === "ar";

      import(/* webpackChunkName: "xlsx" */ "xlsx-js-style")
        .then(
          function (module) {
            const XLSX = module.default || module;

            const header = [
              this.$t("finance.common.code"),
              this.$t("finance.common.name"),
              this.$t("finance.coa.level"),
              this.$t("finance.coa.kind"),
              this.$t("finance.coa.nature"),
              this.$t("finance.coa.closingAccount"),
              this.$t("finance.common.status"),
            ];
            const data = rows.map(
              function (row) {
                return [
                  String(row.account.code),
                  this.displayAccountName(row.account),
                  Number(row.account.level || 1),
                  this.formatKind(row.account.kind),
                  this.formatNature(row.account.nature),
                  this.closingLabel(row.account),
                  row.account.is_active
                    ? this.$t("finance.coa.active")
                    : this.$t("finance.coa.inactive"),
                ];
              }.bind(this)
            );

            const ws = XLSX.utils.aoa_to_sheet([header].concat(data));
            ws["!cols"] = [
              { wch: 12 },
              { wch: 48 },
              { wch: 9 },
              { wch: 10 },
              { wch: 12 },
              { wch: 26 },
              { wch: 10 },
            ];

            const thin = { style: "thin", color: { rgb: "9E9E9E" } };
            const borders = { top: thin, bottom: thin, left: thin, right: thin };
            for (let r = 0; r < data.length + 1; r++) {
              for (let c = 0; c < header.length; c++) {
                const cell = ws[XLSX.utils.encode_cell({ r: r, c: c })];
                if (!cell) {
                  continue;
                }
                if (r === 0) {
                  cell.s = {
                    font: { bold: true },
                    fill: { patternType: "solid", fgColor: { rgb: "ECEFF1" } },
                    border: borders,
                    alignment: { vertical: "center" },
                  };
                  continue;
                }
                const row = rows[r - 1];
                const bold = Number(row.account.level || 1) <= 2;
                cell.s = {
                  font: { bold: bold },
                  border: borders,
                  alignment:
                    c === 1
                      ? { indent: Math.min(row.depth, 15), vertical: "center" }
                      : { vertical: "center" },
                };
              }
            }

            const wb = XLSX.utils.book_new();
            // Sheet reads right-to-left for the Arabic chart.
            wb.Workbook = { Views: [{ RTL: !!isArabic }] };
            XLSX.utils.book_append_sheet(wb, ws, this.$t("finance.coa.pageTitle"));
            XLSX.writeFile(wb, "chart-of-accounts-" + this.exportFileStamp() + ".xlsx");
          }.bind(this)
        )
        .catch(
          function () {
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.coa.failedLoad"), "red");
          }.bind(this)
        );
    },
    escapeHtml(value) {
      return String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    exportPdf() {
      // Dedicated print document instead of printing the app page: no app
      // chrome, repeated table header per page, full tree, RTL-aware. The
      // browser's "Save as PDF" printer renders the Arabic faithfully.
      const rows = this.fullTreeRows();
      const isArabic = this.$i18n && this.$i18n.locale === "ar";
      const esc = this.escapeHtml;
      const title = this.$t("finance.coa.pageTitle");
      // Final-version doc 2026-10-05: company (Damascus) time, not the PC's.
      const stamp = companyNow().format("YYYY-MM-DD HH:mm:ss");

      const headerCells = [
        this.$t("finance.common.code"),
        this.$t("finance.common.name"),
        this.$t("finance.coa.level"),
        this.$t("finance.coa.kind"),
        this.$t("finance.coa.nature"),
        this.$t("finance.common.status"),
      ]
        .map(function (label) {
          return "<th>" + esc(label) + "</th>";
        })
        .join("");

      const bodyRows = rows
        .map(
          function (row) {
            const level = Number(row.account.level || 1);
            const cls = level === 1 ? "lvl-1" : level === 2 ? "lvl-2" : "";
            return (
              '<tr class="' + cls + '">' +
              '<td class="code">' + esc(row.account.code) + "</td>" +
              '<td><span style="padding-inline-start:' + row.depth * 16 + 'px">' +
              esc(this.displayAccountName(row.account)) + "</span></td>" +
              "<td>" + level + "</td>" +
              "<td>" + esc(this.formatKind(row.account.kind)) + "</td>" +
              "<td>" + esc(this.formatNature(row.account.nature)) + "</td>" +
              "<td>" +
              esc(row.account.is_active ? this.$t("finance.coa.active") : this.$t("finance.coa.inactive")) +
              "</td>" +
              "</tr>"
            );
          }.bind(this)
        )
        .join("");

      const doc =
        "<!doctype html>" +
        '<html dir="' + (isArabic ? "rtl" : "ltr") + '" lang="' + (isArabic ? "ar" : "en") + '">' +
        '<head><meta charset="utf-8"><title>' + esc(title) + "</title><style>" +
        'body{font-family:"Segoe UI",Tahoma,Arial,sans-serif;color:#111;margin:0;padding:24px;}' +
        "header{display:flex;justify-content:space-between;align-items:baseline;border-bottom:2px solid #111;padding-bottom:8px;margin-bottom:14px;}" +
        "h1{font-size:20px;margin:0;}" +
        ".meta{font-size:12px;color:#555;}" +
        "table{width:100%;border-collapse:collapse;font-size:12px;}" +
        "th,td{border:1px solid #888;padding:4px 8px;text-align:start;vertical-align:top;}" +
        "thead{display:table-header-group;}" +
        "thead th{background:#eee;font-weight:700;}" +
        "tr{page-break-inside:avoid;}" +
        ".lvl-1 td{font-weight:700;font-size:13px;background:#f3f3f3;}" +
        ".lvl-2 td{font-weight:700;}" +
        ".code{white-space:nowrap;}" +
        "@page{size:A4;margin:12mm;}" +
        "</style></head><body>" +
        "<header><h1>" + esc(title) + '</h1><div class="meta">' +
        esc(this.$t("finance.coa.exportedAt")) + ": " + esc(stamp) + "</div></header>" +
        "<table><thead><tr>" + headerCells + "</tr></thead><tbody>" + bodyRows + "</tbody></table>" +
        "</body></html>";

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.coa.failedLoad"), "red");
        return;
      }
      printWindow.document.write(doc);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(function () {
        printWindow.print();
      }, 400);
    },
    openDeleteDialog(account) {
      // Friendlier local pre-checks; the backend enforces the same rules
      // (plus journal-line / cash-movement references) as the authority.
      if (account.system_key) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.coa.deleteBlockedSystem"), "red");
        return;
      }
      if ((this.childrenByParent[account.id] || []).length) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.coa.deleteBlockedChildren"), "red");
        return;
      }
      this.deleteTarget = account;
      this.deleteDialogOpen = true;
    },
    closeDeleteDialog() {
      this.deleteDialogOpen = false;
      this.deleteTarget = null;
    },
    confirmDelete() {
      if (!this.deleteTarget) {
        this.closeDeleteDialog();
        return;
      }
      this.deleting = true;
      this.axios
        .delete(this.api.ACTIONS.ACCOUNTING_CHART_OF_ACCOUNT(this.deleteTarget.id))
        .then(
          function () {
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.coa.accountDeleted"), "green");
            this.closeDeleteDialog();
            this.loadAccounts();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.coa.failedDelete")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.deleting = false;
          }.bind(this)
        );
    },
    openPeriodClose() {
      this.closeForm = { from: null, to: null };
      this.closePreview = null;
      this.closeDialogOpen = true;
    },
    closePeriodDialog() {
      this.closeDialogOpen = false;
      this.closePreview = null;
    },
    previewPeriodClose() {
      this.runPeriodClose(true);
    },
    confirmPeriodClose() {
      this.runPeriodClose(false);
    },
    runPeriodClose(dryRun) {
      this.closing = true;
      this.axios
        .post(this.api.ACTIONS.ACCOUNTING_PERIOD_CLOSE, {
          from: this.closeForm.from || null,
          to: this.closeForm.to || null,
          dry_run: dryRun,
        })
        .then(
          function (response) {
            if (dryRun) {
              this.closePreview = response.data.data;
            } else {
              window.getApp.$emit("SHOW_TOAST", this.$t("finance.coa.periodCloseDone"), "green");
              this.closePeriodDialog();
              this.loadAccounts();
            }
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.coa.failedSave")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.closing = false;
          }.bind(this)
        );
    },
  },
};
</script>

<style scoped>
/* Table chrome comes from the shared .fin-table layer (finance-compact.css,
   final-version doc 2026-10-05). */
.coa-table-wrap {
  --fin-table-max-h: calc(100vh - 250px);
  min-height: 320px;
}
.coa-parent-row td {
  background: #f5f7fa;
}
/* BRD visual rule: level 1 bold + slightly larger, level 2 bold, 3+ regular */
.coa-level-1 td {
  font-weight: 700;
  font-size: 14px;
}
.coa-level-2 td {
  font-weight: 700;
}
.coa-inactive-row td {
  color: rgba(0, 0, 0, 0.4);
}
.coa-indent {
  display: inline-flex;
  align-items: center;
}
.coa-toggle {
  margin: 0;
  margin-inline-end: 4px;
  height: 22px;
  width: 22px;
}
.coa-toggle-spacer {
  display: inline-block;
  width: 26px;
}
.coa-actions {
  white-space: nowrap;
}
@media print {
  .coa-no-print {
    display: none !important;
  }
}
</style>
